import React from 'react';
import { useTrainingForm } from '../../../hooks/forms/useTrainingForm';
import { useTranslatedContent } from '../../../hooks/useTranslatedContent';
import FormStatus from './status/FormStatus';
import Button from '../../common/Button';
import { Send, User, Mail, Building2, Users, GraduationCap, Calendar, MessageSquare } from 'lucide-react';

const TrainingRequestForm: React.FC = () => {
  const { t } = useTranslatedContent('request');
  const {
    formData,
    loading,
    error,
    success,
    handleSubmit,
    handleChange
  } = useTrainingForm();

  return (
    <form onSubmit={handleSubmit} className="space-y-8" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="group">
          <label htmlFor="name" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
            <User className="h-4 w-4 mr-2 text-blue-600" />
            {t('fields.name')} <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 outline-none hover:border-gray-300"
            placeholder="John Doe"
            value={formData.name}
            onChange={handleChange}
          />
        </div>

        <div className="group">
          <label htmlFor="email" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
            <Mail className="h-4 w-4 mr-2 text-blue-600" />
            {t('fields.email')} <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 outline-none hover:border-gray-300"
            placeholder="john.doe@company.com"
            value={formData.email}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="group">
          <label htmlFor="company" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
            <Building2 className="h-4 w-4 mr-2 text-blue-600" />
            {t('fields.company')} <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            id="company"
            name="company"
            required
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 outline-none hover:border-gray-300"
            placeholder="Company Name"
            value={formData.company}
            onChange={handleChange}
          />
        </div>

        <div className="group">
          <label htmlFor="participants" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
            <Users className="h-4 w-4 mr-2 text-blue-600" />
            {t('fields.participants')} <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="number"
            id="participants"
            name="participants"
            required
            min="1"
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 outline-none hover:border-gray-300"
            placeholder="5"
            value={formData.participants}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="group">
          <label htmlFor="trainingType" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
            <GraduationCap className="h-4 w-4 mr-2 text-blue-600" />
            {t('fields.trainingType')} <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            id="trainingType"
            name="trainingType"
            required
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 outline-none hover:border-gray-300 bg-white cursor-pointer"
            value={formData.trainingType}
            onChange={handleChange}
          >
            <option value="">Select training type...</option>
            <option value="administrator">{t('options.administrator')}</option>
            <option value="endUser">{t('options.endUser')}</option>
            <option value="certification">{t('options.certification')}</option>
            <option value="custom">{t('options.custom')}</option>
          </select>
        </div>

        <div className="group">
          <label htmlFor="preferredDate" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
            <Calendar className="h-4 w-4 mr-2 text-blue-600" />
            {t('fields.preferredDate')}
          </label>
          <input
            type="date"
            id="preferredDate"
            name="preferredDate"
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 outline-none hover:border-gray-300 cursor-pointer"
            value={formData.preferredDate}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="group">
        <label htmlFor="message" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
          <MessageSquare className="h-4 w-4 mr-2 text-blue-600" />
          {t('fields.message')}
        </label>
        <textarea
          id="message"
          name="message"
          rows={5}
          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 outline-none hover:border-gray-300 resize-none"
          placeholder="Tell us more about your training needs, objectives, or any specific requirements..."
          value={formData.message}
          onChange={handleChange}
        />
      </div>

      <FormStatus
        error={error}
        success={success}
        successMessage={t('success')}
      />

      <div className="flex justify-end pt-4">
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          icon={Send}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-4 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
        >
          {loading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('sending')}
            </span>
          ) : (
            t('submit')
          )}
        </Button>
      </div>
    </form>
  );
};

export default TrainingRequestForm;
